import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

const businessDaysFrom = (start, days) => {
  const date = new Date(start);
  let added = 0;
  while (added < days) {
    date.setDate(date.getDate() + 1);
    const day = date.getDay();
    if (day !== 0 && day !== 6) added++;
  }
  return date;
};

const formatShort = (date) => new Intl.DateTimeFormat('es-CL', { day: 'numeric', month: 'short' }).format(date).replace(/\./g, '');

function initEta(root = document) {
  root.querySelectorAll('[data-aa-eta]').forEach((el) => {
    const min = Number(el.dataset.min);
    const max = Number(el.dataset.max);
    const target = el.querySelector('[data-aa-eta-text]');
    if (!target || !min || !max) return;
    // Comuna elegida en "Enviar a" (snippets/aa-shipto.liquid): suma los días extra de su región.
    // Sin comuna no se muestra costo ni fecha (pedido de Elías 18/10 y 28/10): se invita a elegir región y comuna.
    const ship = window.AAShipto?.eta(min, max);
    const cta = el.querySelector('[data-aa-eta-loc]');
    if (cta) cta.hidden = !ship;
    // Precio del despacho (tarifa plana del perfil de envío, igual en todas las comunas; ver aa-shipping-eta).
    const note = el.dataset.priceNote ? ` (${escapeHtml(el.dataset.priceNote)})` : '';
    const price = el.dataset.price ? `<strong class="aa-pd-eta__price">${escapeHtml(el.dataset.price)}</strong>${note}` : '';
    if (!ship) {
      target.innerHTML = '<button type="button" class="aa-pd-eta__loc aa-pd-eta__loc--in" data-aa-shipto-open>Elige tu región y comuna</button> para ver el costo del envío y la fecha de entrega';
      return;
    }
    const place = `<span class="aa-pd-eta__place">${escapeHtml(ship.comuna)}</span>`;
    if (!ship.ok) {
      target.innerHTML = `Recíbelo en ${place}: por ahora sin despacho`;
      return;
    }
    const now = new Date();
    // Todo en una línea: comuna · costo · fecha (cada trozo no se corta por dentro).
    const sep = '<span class="aa-pd-eta__sep" aria-hidden="true"></span>'; // p. ej. "Providencia · $2.990 · Llega 30 oct – 4 nov"
    // La comuna también abre el selector: en celular reemplaza al botón "Cambiar" y la línea no se corta.
    const chunks = [`<button type="button" class="aa-pd-eta__chunk aa-pd-eta__comuna" data-aa-shipto-open aria-label="Cambiar comuna (${escapeHtml(ship.comuna)})">${place}</button>`];
    if (price) chunks.push(`<span class="aa-pd-eta__chunk">${price}</span>`);
    chunks.push(`<span class="aa-pd-eta__chunk">Llega entre <strong>${formatShort(businessDaysFrom(now, ship.min))}</strong> y <strong>${formatShort(businessDaysFrom(now, ship.max))}</strong></span>`);
    target.innerHTML = chunks.join(sep);
  });
}
document.addEventListener('aa:shipto', () => initEta());

const escapeHtml = (s) => String(s).replace(/[&<>"']/g, (ch) => `&#${ch.charCodeAt(0)};`);

function initTabs(root = document) {
  root.querySelectorAll('[data-aa-tabs]:not([data-aa-ready])').forEach((wrap) => {
    wrap.dataset.aaReady = 'true';
    const tabs = [...wrap.querySelectorAll('[role="tab"]')];
    const select = (tab, focus = false) => {
      for (const t of tabs) {
        const on = t === tab;
        t.setAttribute('aria-selected', String(on));
        t.tabIndex = on ? 0 : -1;
        const panel = wrap.querySelector(`#${t.getAttribute('aria-controls')}`);
        if (panel) panel.hidden = !on;
      }
      if (focus) tab.focus();
    };
    tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => select(tab));
      tab.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
        event.preventDefault();
        const next = tabs[(i + (event.key === 'ArrowRight' ? 1 : tabs.length - 1)) % tabs.length];
        select(next, true);
      });
    });
    if (tabs.length) select(tabs[0]);
  });
}

// Keep variant-dependent Andes blocks (price, badges) in sync with Horizon's variant picker.
document.addEventListener(StandardEvents.productSelect, (event) => {
  if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
  event.promise
    ?.then(({ detail }) => {
      const html = detail?.html;
      if (!html) return;
      document.querySelectorAll('[data-aa-sync]').forEach((el) => {
        const fresh = html.querySelector(`[data-aa-sync="${el.dataset.aaSync}"]`);
        if (fresh) el.innerHTML = fresh.innerHTML;
      });
    })
    .catch(() => {});
});

// ---- Add to cart celebration (template "andes"): loading shimmer on click, then — only once the
// cart confirms the add — a colour burst, a check on the button and the product flying to the cart icon.
const ATC_SCOPE = 'main[data-template^="product.andes"]';
const CONFETTI = ['#22B573', '#8FE3BD', '#FFD23F', '#FF6B6B', '#4D96FF', '#C77DFF', '#FF9F1C', '#0B131C'];
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
let pending = null;

const centerOf = (el) => {
  const r = el.getBoundingClientRect();
  return { x: r.left + r.width / 2, y: r.top + r.height / 2, r };
};

function burst(btn) {
  const { r } = centerOf(btn);
  const layer = document.createElement('div');
  layer.className = 'aa-confetti';
  layer.setAttribute('aria-hidden', 'true');
  document.body.append(layer);
  const pieces = 42;
  const runs = [];
  for (let i = 0; i < pieces; i++) {
    const p = document.createElement('i');
    const size = 5 + Math.random() * 6;
    p.style.cssText = `left:${r.left + r.width * (0.25 + Math.random() * 0.5)}px;top:${r.top + r.height / 2}px;width:${size}px;height:${Math.random() < 0.4 ? size : size * 0.45}px;background:${CONFETTI[i % CONFETTI.length]};border-radius:${Math.random() < 0.35 ? '50%' : '2px'}`;
    layer.append(p);
    const angle = (-90 + (Math.random() - 0.5) * 150) * (Math.PI / 180);
    const dist = 70 + Math.random() * 120;
    const dx = Math.cos(angle) * dist;
    const dy = Math.sin(angle) * dist;
    const spin = (Math.random() - 0.5) * 900;
    runs.push(
      p.animate(
        [
          { transform: 'translate(-50%,-50%) scale(.4) rotate(0deg)', opacity: 1 },
          { transform: `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px)) scale(1) rotate(${spin * 0.6}deg)`, opacity: 1, offset: 0.55 },
          { transform: `translate(calc(-50% + ${dx * 1.15}px), calc(-50% + ${dy + 90}px)) scale(.8) rotate(${spin}deg)`, opacity: 0 },
        ],
        { duration: 1000 + Math.random() * 500, easing: 'cubic-bezier(.15,.7,.3,1)', fill: 'forwards' }
      ).finished
    );
  }
  Promise.allSettled(runs).then(() => layer.remove());
}

function markAdded(btn) {
  let done = btn.querySelector('.aa-atc-done');
  if (!done) {
    done = document.createElement('span');
    done.className = 'aa-atc-done';
    done.setAttribute('aria-hidden', 'true');
    done.innerHTML =
      '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><path class="aa-atc-done__check" d="M5 12.5l4.2 4.2L19 7"/></svg><span>¡Agregado!</span>';
    btn.append(done);
  }
  btn.classList.remove('aa-atc--added');
  void btn.offsetWidth; // restart the animation on repeat clicks
  btn.classList.add('aa-atc--added');
  clearTimeout(btn._aaAddedTimer);
  btn._aaAddedTimer = setTimeout(() => btn.classList.remove('aa-atc--added'), 1900);
}

async function flyToCart(btn, image) {
  const group = document.getElementById('header-group');
  if (group?.classList.contains('aa-topbar--hidden')) {
    group.classList.remove('aa-topbar--hidden');
    await new Promise((resolve) => setTimeout(resolve, 320));
  }
  const icon = document.querySelector('.header-actions__cart-icon');
  if (!icon) return;
  const from = centerOf(btn);
  const to = centerOf(icon);
  const ghost = document.createElement('div');
  ghost.className = 'aa-fly';
  ghost.setAttribute('aria-hidden', 'true');
  if (image) ghost.style.backgroundImage = `url("${image}")`;
  ghost.style.left = `${from.x}px`;
  ghost.style.top = `${from.y}px`;
  document.body.append(ghost);
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const lift = Math.min(-80, dy * 0.35 - 120);
  await ghost
    .animate(
      [
        { transform: 'translate(-50%,-50%) scale(.2)', opacity: 0 },
        { transform: 'translate(-50%,-50%) scale(1.15)', opacity: 1, offset: 0.14 },
        { transform: `translate(calc(-50% + ${dx * 0.45}px), calc(-50% + ${lift}px)) scale(.9) rotate(-8deg)`, offset: 0.55 },
        { transform: `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px)) scale(.22) rotate(10deg)`, opacity: 0.9 },
      ],
      { duration: 950, easing: 'cubic-bezier(.45,0,.25,1)', fill: 'forwards' }
    )
    .finished.catch(() => {});
  ghost.remove();
  icon.classList.remove('aa-cart-bump');
  void icon.offsetWidth;
  icon.classList.add('aa-cart-bump');
  setTimeout(() => icon.classList.remove('aa-cart-bump'), 900);
}

function celebrate({ btn, comp, origin }) {
  markAdded(btn);
  if (reduceMotion.matches) {
    window.aaFlightPending = false;
    return;
  }
  // Clicks relayed from the floating sticky button animate from it (the main button may be off screen).
  const from = origin && origin.getBoundingClientRect().width ? origin : btn;
  burst(from);
  // The cart bubble (patched in aa-andes.js) waits for aaFlightPending, so the count changes on landing.
  setTimeout(async () => {
    try {
      await flyToCart(from, comp?.dataset.productVariantMedia);
    } finally {
      window.aaFlightPending = false;
    }
  }, 420);
}

// "Lleva también" (blocks/aa-addons): the checked add-ons go to the cart right after the main product.
// With a source element it also fires Horizon's cart event, so the cart bubble and drawer refresh themselves.
const addAddons = async (source) => {
  const boxes = [...document.querySelectorAll('[data-aa-addon]:checked')];
  if (!boxes.length) return;
  const root = window.Shopify?.routes?.root || '/';
  const items = boxes.map((b) => ({ id: Number(b.value), quantity: 1 }));
  let deferred = null;
  if (source) {
    deferred = CartLinesUpdateEvent.createPromise();
    source.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: 1 })),
        promise: deferred.promise,
      })
    );
  }
  try {
    const res = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items }),
    });
    if (deferred) {
      const cart = await fetch(`${root}cart.js`).then((r) => r.json());
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { didError: !res.ok, source: 'aa-addons', itemCount: items.length, items: cart.items },
      });
    }
    if (res.ok) boxes.forEach((b) => (b.checked = false));
  } catch (error) {
    deferred?.reject(error);
  }
};

// Capture phase: runs before Horizon's own handler, so its default fly-to-cart can be switched off here.
document.addEventListener(
  'click',
  (event) => {
    const btn = event.target instanceof Element ? event.target.closest(`${ATC_SCOPE} .add-to-cart-button:not(.quick-add__button):not(.sticky-add-to-cart__button)`) : null;
    if (!btn || btn.disabled) return;
    const form = btn.closest('form');
    if (form && !form.checkValidity()) return;
    const comp = btn.closest('add-to-cart-component');
    if (comp) comp.dataset.addToCartAnimation = 'false';
    btn.classList.add('aa-atc--loading');
    clearTimeout(btn._aaLoadingTimer);
    btn._aaLoadingTimer = setTimeout(() => btn.classList.remove('aa-atc--loading'), 8000);
    const origin = btn.dataset.puppet === 'true' ? document.querySelector('.sticky-add-to-cart__bar[data-stuck="true"] .sticky-add-to-cart__button') : null;
    pending = { btn, comp, origin };
    window.aaFlightPending = true;
    clearTimeout(window.aaFlightTimer);
    window.aaFlightTimer = setTimeout(() => (window.aaFlightPending = false), 6000);
  },
  true
);

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  const current = pending;
  if (!current || !(event.target instanceof Node) || !event.target.contains(current.btn)) return;
  pending = null;
  const settle = () => {
    clearTimeout(current.btn._aaLoadingTimer);
    current.btn.classList.remove('aa-atc--loading');
  };
  const release = () => (window.aaFlightPending = false);
  event.promise
    ?.then((result) => {
      settle();
      if (!result?.detail?.didError) {
        celebrate(current);
        addAddons(document.querySelector('.aa-addons'));
      } else release();
    })
    .catch(() => {
      settle();
      release();
    });
});

// "Comprar ahora" (snippets/aa-buy-now): adds the form's variant and quantity, then goes straight to /cart.
// Errors (e.g. not enough stock) show in Horizon's own error line; a network failure falls back to Add to cart.
document.addEventListener('click', async (event) => {
  const btn = event.target instanceof Element ? event.target.closest('[data-aa-buy-now]') : null;
  if (!btn || btn.disabled || btn.classList.contains('is-loading')) return;
  const form = btn.closest('form');
  const atc = form?.querySelector('.add-to-cart-button');
  if (!form || atc?.disabled) return;
  if (!form.checkValidity()) return form.reportValidity();
  const routes = (typeof Theme !== 'undefined' && Theme.routes) || {};
  const error = form.querySelector('.product-form-text__error');
  btn.classList.add('is-loading');
  btn.setAttribute('aria-busy', 'true');
  const reset = () => {
    btn.classList.remove('is-loading');
    btn.removeAttribute('aria-busy');
  };
  try {
    const res = await fetch(routes.cart_add_url || '/cart/add.js', {
      method: 'POST',
      body: new FormData(form),
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    });
    if (res.ok) {
      await addAddons();
      // Horizon's cart icon trusts a count saved in sessionStorage for 10 s over the server's; this add
      // fires no cart event, so that saved count is now stale and would hide the new item on /cart.
      try {
        sessionStorage.removeItem('cart-count');
      } catch {}
      window.location.href = routes.cart_url || '/cart';
      return;
    }
    const data = await res.json().catch(() => ({}));
    reset();
    if (error) {
      let msg = error.querySelector('[data-aa-buy-error]');
      if (!msg) {
        msg = document.createElement('span');
        msg.dataset.aaBuyError = '';
        error.append(msg);
      }
      msg.textContent = data.description || data.message || 'No pudimos agregar el producto. Inténtalo de nuevo.';
      error.classList.remove('hidden');
    }
  } catch {
    reset();
    atc?.click();
  }
});
// Back button (page restored from cache) must not show the button still loading
window.addEventListener('pageshow', () => document.querySelectorAll('[data-aa-buy-now].is-loading').forEach((b) => b.classList.remove('is-loading')));

// "Avísame cuando vuelva" (blocks/aa-back-in-stock): posts Shopify's customer form in place. If Shopify
// answers with a captcha challenge, it falls back to a normal post and a sessionStorage flag tells this
// block (and not the footer newsletter, which shares posted_successfully?) to show the success state.
const BIS_FLAG = 'aa-bis';

function initBackInStock(root = document) {
  let flag = null;
  try {
    flag = sessionStorage.getItem(BIS_FLAG);
    sessionStorage.removeItem(BIS_FLAG);
  } catch {}
  root.querySelectorAll('[data-aa-bis]').forEach((wrap) => {
    const box = wrap.querySelector('[data-aa-bis-posted]');
    const done = wrap.querySelector('[data-aa-bis-done]');
    if (!box || !done || flag === wrap.dataset.handle) return;
    box.hidden = false;
    done.hidden = true;
  });
}

document.addEventListener('submit', async (event) => {
  const form = event.target instanceof HTMLFormElement ? event.target : null;
  const wrap = form?.closest('[data-aa-bis]');
  if (!form || !wrap) return;
  event.preventDefault();
  const box = wrap.querySelector('.aa-bis__box');
  const done = wrap.querySelector('[data-aa-bis-done]');
  const error = wrap.querySelector('[data-aa-bis-error]');
  const input = form.querySelector('input[type="email"]');
  const button = form.querySelector('.aa-bis__submit');
  const native = () => {
    try {
      sessionStorage.setItem(BIS_FLAG, wrap.dataset.handle || '');
    } catch {}
    form.submit();
  };
  if (button) button.disabled = true;
  wrap.classList.add('is-sending');
  if (error) error.hidden = true;
  try {
    const res = await fetch(form.action, { method: 'POST', body: new FormData(form), headers: { Accept: 'text/html' } });
    if (!res.ok || res.url.includes('/challenge')) return native();
    const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
    const sent = doc.getElementById(form.id);
    const err = sent?.querySelector('[data-aa-bis-error]:not([hidden])');
    if (err && error) {
      error.textContent = err.textContent.trim() || 'Revisa tu correo e inténtalo de nuevo.';
      error.hidden = false;
      input?.setAttribute('aria-invalid', 'true');
      input?.focus();
      return;
    }
    if (!res.url.includes('customer_posted=true') && !sent?.querySelector('[data-aa-bis-posted]')) return native();
    input?.removeAttribute('aria-invalid');
    if (box) box.hidden = true;
    if (done) done.hidden = false;
  } catch {
    native();
  } finally {
    if (button) button.disabled = false;
    wrap.classList.remove('is-sending');
  }
});

initEta();
initTabs();
initBackInStock();
document.addEventListener('shopify:section:load', (event) => {
  initEta(event.target);
  initTabs(event.target);
});

// ---- Gallery (template "andes"): Mercado Libre style zoom with a mouse. The photo stays as is with a
// lens over the area being examined, and that area is shown enlarged in a panel beside the photo (over the
// product details). It never enlarges a photo past its own resolution (at most 1 photo pixel per screen
// point), so it can't look pixelated; photos too small for 1.3x get no zoom. A click opens Horizon's
// full-screen viewer. If there's no room beside the photo, the enlarged view shows inside the photo.
const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
const MAIN_SCOPE = 'main[data-template^="product.andes"]';
document.addEventListener('pointerover', (event) => {
  if (!finePointer.matches || event.pointerType !== 'mouse') return;
  const box = event.target instanceof Element ? event.target.closest(`${MAIN_SCOPE} media-gallery .product-media-container--image`) : null;
  if (!box || box._aaZoom || box.closest('.dialog-zoomed-gallery')) return;
  const img = box.querySelector('img');
  if (!img || !img.complete) return;
  const gallery = box.closest('media-gallery');
  // Ask the CDN for up to 2000 px: it never upscales, so the file's real width tells how far we can zoom.
  let src = img.currentSrc || img.src;
  try {
    const u = new URL(src, location.href);
    u.searchParams.set('width', '2000');
    u.searchParams.delete('height');
    src = u.href;
  } catch {}
  const state = { lens: null, pane: null, k: 0, last: event };
  box._aaZoom = state;

  // Geometry: the photo may be cropped (object-fit: cover) inside its square, so work in the photo's own
  // drawn size (c) and offset (o). k = zoom, never more than the file's real pixels allow.
  const place = (e) => {
    state.last = e;
    if (!state.k) return;
    const r = img.getBoundingClientRect();
    const { k, lw, lh, c } = state;
    const lx = Math.min(r.width - lw, Math.max(0, e.clientX - r.left - lw / 2));
    const ly = Math.min(r.height - lh, Math.max(0, e.clientY - r.top - lh / 2));
    if (state.lens) {
      const b = box.getBoundingClientRect();
      state.lens.style.transform = `translate(${(r.left - b.left + lx).toFixed(1)}px, ${(r.top - b.top + ly).toFixed(1)}px)`;
    }
    state.pane.style.backgroundPosition = `${(-(lx - c.ox) * k).toFixed(1)}px ${(-(ly - c.oy) * k).toFixed(1)}px`;
  };

  const setup = (nw, nh) => {
    const r = img.getBoundingClientRect();
    const fit = getComputedStyle(img).objectFit;
    const sc = fit === 'contain' ? Math.min(r.width / nw, r.height / nh) : Math.max(r.width / nw, r.height / nh);
    const c = { w: nw * sc, h: nh * sc };
    c.ox = (r.width - c.w) / 2;
    c.oy = (r.height - c.h) / 2;
    const k = Math.min(2.5, nw / c.w);
    if (k < 1.25) return;
    const g = gallery.getBoundingClientRect();
    const room = window.innerWidth - g.right - 24;
    const pane = document.createElement('span');
    pane.className = 'aa-zoom';
    pane.setAttribute('aria-hidden', 'true');
    pane.style.backgroundImage = `url("${src}")`;
    pane.style.backgroundSize = `${(c.w * k).toFixed(1)}px ${(c.h * k).toFixed(1)}px`;
    let pw = r.width;
    let ph = r.height;
    if (room < 320) {
      // No room beside the photo: the enlarged view covers the photo itself.
      const b = box.getBoundingClientRect();
      pane.style.cssText += `left:${r.left - b.left}px;top:${r.top - b.top}px;width:${pw}px;height:${ph}px;`;
      box.append(pane);
    } else {
      // Pane beside the photo (over the details); the lens frames exactly what the pane shows.
      pw = Math.min(r.width * 1.15, room);
      const top = Math.max(12, r.top);
      ph = Math.min(r.height, window.innerHeight - top - 12);
      pane.classList.add('aa-zoom--side');
      pane.style.cssText += `left:${g.right + 24}px;top:${top}px;width:${pw}px;height:${ph}px;`;
      state.lens = document.createElement('span');
      state.lens.className = 'aa-zoom-lens';
      state.lens.setAttribute('aria-hidden', 'true');
      state.lens.style.width = `${Math.min(r.width, pw / k)}px`;
      state.lens.style.height = `${Math.min(r.height, ph / k)}px`;
      box.append(state.lens);
      document.body.append(pane);
    }
    Object.assign(state, { pane, k, c, lw: Math.min(r.width, pw / k), lh: Math.min(r.height, ph / k) });
    place(state.last);
    requestAnimationFrame(() => {
      pane.classList.add('is-on');
      state.lens?.classList.add('is-on');
    });
  };

  const leave = () => {
    box.removeEventListener('pointermove', place);
    box.removeEventListener('pointerleave', leave);
    window.removeEventListener('scroll', leave);
    box._aaZoom = null;
    for (const el of [state.pane, state.lens]) {
      if (!el) continue;
      el.classList.remove('is-on');
      setTimeout(() => el.remove(), 200);
    }
  };
  box.addEventListener('pointermove', place);
  box.addEventListener('pointerleave', leave);
  window.addEventListener('scroll', leave, { passive: true, once: true });
  const pre = new Image();
  pre.onload = () => box._aaZoom === state && setup(pre.naturalWidth, pre.naturalHeight);
  pre.src = src;
});

// The gallery heart is re-rendered when the colour changes: re-apply the saved state (aa-andes.js initFavorites).
const syncHearts = () => {
  let saved = [];
  try {
    saved = JSON.parse(localStorage.getItem('aa-favs') || '[]').map((f) => f.handle);
  } catch {}
  document.querySelectorAll('.aa-fav--media[data-aa-fav]').forEach((b) => {
    const on = saved.includes(b.dataset.handle);
    b.setAttribute('aria-pressed', String(on));
    b.setAttribute('aria-label', `${on ? 'Quitar' : 'Guardar'} ${b.dataset.title} ${on ? 'de' : 'en'} favoritos`);
  });
};
document.addEventListener(StandardEvents.productSelect, (event) => {
  event.promise?.then(() => setTimeout(syncHearts, 60)).catch(() => {});
});

// Mobile dots: with many photos only a window of dots is visible; keep the current one in view.
const centerDots = () => {
  document.querySelectorAll(`${MAIN_SCOPE} media-gallery slideshow-controls`).forEach((ctrl) => {
    const list = ctrl.querySelector('.slideshow-controls__dots');
    const cur = list?.querySelector('[aria-selected="true"]');
    if (!list || !cur || list.scrollWidth <= list.clientWidth) return;
    list.scrollTo({ left: cur.offsetLeft - list.clientWidth / 2 + cur.offsetWidth / 2, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
  });
};
document.addEventListener('slideshow:select', () => requestAnimationFrame(centerDots), true);

// ---- Reseñas (Judge.me): fecha en formato chileno y con hora ("28/10/2026 · 00:36"). La hora sale de los datos
// que Judge.me deja en la página (jdgm.data.reviewWidget); si una reseña no está ahí, solo se da vuelta la fecha.
const reviewStamp = (iso) => {
  const d = new Date(iso);
  const day = new Intl.DateTimeFormat('es-CL', { timeZone: 'America/Santiago', day: '2-digit', month: '2-digit', year: 'numeric' }).format(d).replace(/-/g, '/');
  const time = new Intl.DateTimeFormat('es-CL', { timeZone: 'America/Santiago', hour: '2-digit', minute: '2-digit', hour12: false }).format(d);
  return `${day} · ${time}`;
};
const formatReviewDates = (widget) => {
  const data = Object.values(window.jdgm?.data?.reviewWidget || {}).flatMap((w) => w?.reviews || []);
  widget.querySelectorAll('.jm-review-item').forEach((item) => {
    const name = item.querySelector('.jm-reviewer-info__name')?.textContent.trim();
    const dateEl = item.querySelector('.jm-reviewer-info__details > :last-child .jm-text');
    if (!dateEl) return;
    const raw = dateEl.textContent.trim();
    if (dateEl.dataset.aaStamp === raw) return; // ya formateada (evita repetir con el observador)
    const body = item.querySelector('.jm-review-content__body')?.textContent.trim();
    const match = data.find((r) => r.reviewer_name === name && (!body || r.body?.trim() === body));
    let text = raw;
    if (match?.created_at) text = reviewStamp(match.created_at);
    else {
      const us = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/); // Judge.me muestra mes/día/año
      if (us) text = `${us[2].padStart(2, '0')}/${us[1].padStart(2, '0')}/${us[3]}`;
    }
    dateEl.textContent = text;
    dateEl.dataset.aaStamp = text;
  });
};
document.querySelectorAll('.aa-reviews__widget').forEach((widget) => {
  const run = () => formatReviewDates(widget);
  new MutationObserver(run).observe(widget, { childList: true, subtree: true });
  run();
});

// ---- "También te puede interesar": the product data is requested only when the visitor gets close to the
// section; photos are lazy, so in the carousel each card's photo downloads as it slides into view.
const initRelatedCarousel = (section) => {
  const track = section.querySelector('[data-aa-related-track]');
  const prev = section.querySelector('[data-aa-related-prev]');
  const next = section.querySelector('[data-aa-related-next]');
  if (!track || !prev || !next) return;
  const update = () => {
    const max = track.scrollWidth - track.clientWidth - 2;
    prev.disabled = track.scrollLeft <= 2;
    next.disabled = track.scrollLeft >= max;
  };
  const step = (dir) => {
    const card = track.querySelector('.aa-related__item');
    const by = card ? card.getBoundingClientRect().width + 14 : track.clientWidth * 0.8;
    track.scrollBy({ left: dir * by * Math.max(1, Math.floor(track.clientWidth / by)), behavior: reduceMotion.matches ? 'auto' : 'smooth' });
  };
  prev.onclick = () => step(-1);
  next.onclick = () => step(1);
  track.addEventListener('scroll', update, { passive: true });
  window.addEventListener('resize', update, { passive: true });
  update();
};
const loadRelated = async (section) => {
  if (section.dataset.aaState) return;
  section.dataset.aaState = 'loading';
  try {
    const res = await fetch(section.dataset.url);
    if (!res.ok) throw new Error(String(res.status));
    const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
    const fresh = doc.querySelector('[data-aa-related]');
    if (!fresh || !fresh.querySelector('[data-aa-related-body] > *')) throw new Error('empty');
    section.replaceChildren(...fresh.childNodes);
    section.dataset.aaState = 'ready';
    section.querySelectorAll('.aa-rv').forEach((el) => el.classList.add('is-in'));
    initRelatedCarousel(section);
  } catch {
    section.hidden = true;
  }
};
const watchRelated = (root = document) => {
  root.querySelectorAll('[data-aa-related]:not([data-aa-state])').forEach((section) => {
    if (!('IntersectionObserver' in window)) return loadRelated(section);
    const io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        io.disconnect();
        loadRelated(section);
      },
      { rootMargin: '400px 0px' }
    );
    io.observe(section);
  });
};
watchRelated();

// ---- "Ver medios de pago" (junto al precio): ventana nativa <dialog> que entra y sale suave; se cierra con
// la X, Esc o tocando fuera. La salida espera su animación antes de cerrar (sin animación si se pide menos movimiento).
const closePay = (dlg) => {
  if (!dlg.open || dlg.classList.contains('is-closing')) return;
  if (reduceMotion.matches) return dlg.close();
  dlg.classList.add('is-closing');
  const done = () => {
    dlg.classList.remove('is-closing');
    dlg.close();
  };
  dlg.addEventListener('animationend', done, { once: true });
  setTimeout(() => dlg.classList.contains('is-closing') && done(), 450);
};
document.addEventListener('click', (event) => {
  const t = event.target instanceof Element ? event.target : null;
  const open = t?.closest('[data-aa-pay-open]');
  if (open) {
    document.getElementById(open.getAttribute('aria-controls'))?.showModal();
    return;
  }
  const dlg = t?.closest('.aa-pay-dlg');
  if (dlg && (t.closest('[data-aa-pay-close]') || t === dlg)) closePay(dlg);
});
document.addEventListener(
  'cancel',
  (event) => {
    if (!(event.target instanceof HTMLDialogElement) || !event.target.matches('.aa-pay-dlg, .aa-cmp-dlg')) return;
    event.preventDefault();
    closePay(event.target);
  },
  true
);

// ---- Simulador de cuotas bajo el precio: valor de cada cuota del precio actual (cambia con la variante).
// Hasta data-free cuotas se rotulan "sin interés"; sobre eso es un valor referencial porque el banco puede cobrar interés.
// Usa el mismo separador de miles que el precio de la tienda ("$9.990" o "$9,990"), para que no se vean dos formatos.
const fmt = { ',': new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }), '.': new Intl.NumberFormat('es-CL', { maximumFractionDigits: 0 }) };
const updateInstallments = (box) => {
  const header = box.closest('.aa-pd-header');
  const now = header?.querySelector('[data-aa-cents]');
  const cents = Number(now?.dataset.aaCents);
  const nf = fmt[/\d([.,])\d{3}/.exec(now?.textContent || '')?.[1] || '.'];
  const clp = { format: (v) => '$' + nf.format(v) };
  const select = box.querySelector('[data-aa-inst-select]');
  const text = box.querySelector('[data-aa-inst-text]');
  if (!cents || !select || !text) return;
  const n = Number(select.value);
  const free = Number(box.dataset.free) || 0;
  const each = clp.format(Math.ceil(cents / 100 / n));
  const via = box.dataset.via ? ` pagando con ${box.dataset.via}` : '';
  const note = (t) => `<span class="aa-pd-inst__note">${t}</span>`;
  const upTo = free > 1 && via ? note(`Hasta ${free} cuotas sin interés${via}.`) : '';
  if (n === 1) text.innerHTML = `<strong>1 pago</strong> de ${clp.format(cents / 100)}${upTo}`;
  else if (n <= free) text.innerHTML = `<strong>${n} cuotas sin interés</strong> de ${each}${upTo}`;
  else
    text.innerHTML = `<strong>${n} cuotas</strong> de ${each} aprox.${note(
      free > 1 ? `Sin interés solo hasta ${free} cuotas${via}. Sobre eso, tu banco puede cobrar interés.` : 'Valor referencial: tu banco puede cobrar interés.'
    )}`;
};
const initInstallments = (root = document) => {
  root.querySelectorAll('[data-aa-inst]:not([data-aa-ready])').forEach((box) => {
    box.dataset.aaReady = '1';
    box.querySelector('[data-aa-inst-select]')?.addEventListener('change', () => updateInstallments(box));
    const price = box.closest('.aa-pd-header')?.querySelector('[data-aa-sync]');
    if (price) new MutationObserver(() => updateInstallments(box)).observe(price, { childList: true, subtree: true });
    updateInstallments(box);
  });
};
initInstallments();



// ---- Barra de envío gratis (aa-product-badges): al agregar algo al carrito se vuelve a pedir el bloque y la barra
// avanza desde donde estaba, late y muestra "+X%" para motivar a seguir sumando.
const refreshFsBar = async () => {
  const bar = document.querySelector('[data-aa-fsbar]');
  const section = bar?.closest('.shopify-section');
  if (!bar || !section) return;
  const url = new URL(window.location.href);
  url.searchParams.set('section_id', section.id.replace(/^shopify-section-/, ''));
  try {
    const html = await fetch(url).then((r) => (r.ok ? r.text() : Promise.reject()));
    const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector(`[data-aa-sync="${bar.dataset.aaSync}"]`);
    if (!fresh) return;
    const before = Number(bar.dataset.pct) || 0;
    const after = Number(fresh.dataset.pct) || 0;
    const oldFill = bar.querySelector('.aa-fsbar__fill')?.style.getPropertyValue('--aa-fs');
    bar.innerHTML = fresh.innerHTML;
    bar.className = `${fresh.className} aa-fsbar--live`;
    bar.dataset.pct = String(after);
    const fill = bar.querySelector('.aa-fsbar__fill');
    if (fill && oldFill && !reduceMotion.matches) {
      const target = fill.style.getPropertyValue('--aa-fs');
      fill.style.setProperty('--aa-fs', oldFill);
      void fill.offsetWidth;
      requestAnimationFrame(() => fill.style.setProperty('--aa-fs', target));
    }
    if (after > before) {
      bar.classList.remove('is-bump');
      void bar.offsetWidth;
      bar.classList.add('is-bump');
      const plus = document.createElement('span');
      plus.className = 'aa-fsbar__plus';
      plus.textContent = after >= 100 ? '¡Envío gratis!' : `+${after - before}%`;
      bar.append(plus);
      setTimeout(() => plus.remove(), 2300);
    }
  } catch {}
};
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (!document.querySelector('[data-aa-fsbar]')) return;
  event.promise?.then((r) => !r?.detail?.didError && setTimeout(refreshFsBar, 150)).catch(() => {});
});

// ---- Ventana para elegir variante (color, talla…) desde "Complementa tu compra": círculos de color, píldoras de talla,
// foto y precio que cambian con la elección; "Agregar al carrito" usa el quick add de aa-andes.js (data-aa-quick-add).
let pickerDlg = null;
let pick = null; // { data, valid, sel, opener }
const esc = (t) => String(t).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);
const findVariant = (opts) => pick.data.variants.find((v) => v.opts.every((o, i) => o === opts[i]));
// Una opción se puede elegir si existe alguna variante disponible con ella y las elecciones anteriores.
const canPick = (i, val) => pick.valid.some((v) => v.opts[i] === val && v.opts.slice(0, i).every((o, j) => o === pick.sel[j]));
const renderPicker = () => {
  const { data, sel } = pick;
  const v = findVariant(sel);
  const ok = !!v?.ok;
  pickerDlg.innerHTML = `
    <div class="aa-cmp-dlg__box">
      <button type="button" class="aa-cmp-dlg__x" data-aa-pick-close aria-label="Cerrar"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg></button>
      <div class="aa-cmp-dlg__top">
        <img class="aa-cmp-dlg__img" src="${esc(v?.img || data.image)}" alt="" width="120" height="120">
        <div class="aa-cmp-dlg__info">
          <a class="aa-cmp-dlg__title" href="${esc(data.url)}">${esc(data.title)}</a>
          <p class="aa-cmp-dlg__price">${esc(v?.price || '')}</p>
        </div>
      </div>
      ${data.options
        .map((o, i) => {
          if (o.values.length < 2 && !o.color) return '';
          const vals = o.values
            .map((val) => {
              const on = sel[i] === val.name;
              const dot = o.color ? `<span class="aa-cmp-dlg__dot"${val.hex ? ` style="--c:${esc(val.hex)}"` : ''}></span>` : '';
              return `<button type="button" class="aa-cmp-dlg__val${on ? ' is-on' : ''}" data-i="${i}" data-v="${esc(val.name)}" aria-pressed="${on}"${canPick(i, val.name) ? '' : ' disabled'} title="${esc(val.name)}">${dot}<span>${esc(val.name)}</span></button>`;
            })
            .join('');
          return `<fieldset class="aa-cmp-dlg__opt"><legend>${esc(o.name)}: <b>${esc(sel[i])}</b></legend><div class="aa-cmp-dlg__vals${o.color ? ' is-color' : ''}">${vals}</div></fieldset>`;
        })
        .join('')}
      <button type="button" class="aa-cmp-dlg__add" ${ok ? `data-aa-quick-add data-variant-id="${v.id}" data-image="${esc(v.img || data.image)}"` : 'disabled'}>
        <span class="aa-cmp-dlg__add-txt">${ok ? 'Agregar al carrito' : 'No disponible'}</span><span class="aa-cmp-dlg__add-ok">¡Agregado a tu carrito!</span>
      </button>
    </div>`;
};
const openVariantPicker = (data, opener) => {
  if (!pickerDlg) {
    pickerDlg = document.createElement('dialog');
    pickerDlg.className = 'aa-cmp-dlg';
    pickerDlg.setAttribute('aria-label', 'Elige tu opción');
    document.body.append(pickerDlg);
    pickerDlg.addEventListener('click', (e) => {
      if (e.target === pickerDlg || e.target.closest('[data-aa-pick-close]')) return closePay(pickerDlg);
      const b = e.target.closest('.aa-cmp-dlg__val');
      if (!b || b.disabled) return;
      const i = Number(b.dataset.i);
      pick.sel[i] = b.dataset.v;
      // Si la combinación no está disponible, ajusta las opciones siguientes a la primera que sí lo esté.
      if (!findVariant(pick.sel)?.ok) {
        const alt = pick.valid.find((v) => v.opts.slice(0, i + 1).every((o, j) => o === pick.sel[j]));
        if (alt) pick.sel = [...alt.opts];
      }
      renderPicker();
    });
    // aa-andes.js agrega y marca el botón con .is-added: entonces se celebra y la ventana se cierra sola.
    pickerDlg.addEventListener('pointerdown', (e) => {
      const add = e.target.closest('.aa-cmp-dlg__add[data-aa-quick-add]');
      if (!add) return;
      const opener = pick.opener;
      const watch = new MutationObserver(() => {
        if (!add.classList.contains('is-added')) return;
        watch.disconnect();
        opener?.classList.add('is-added');
        setTimeout(() => opener?.classList.remove('is-added'), 2200);
        setTimeout(() => closePay(pickerDlg), 1100);
      });
      watch.observe(add, { attributes: true, attributeFilter: ['class'] });
    });
  }
  const valid = data.variants.filter((v) => v.ok);
  pick = { data, valid, sel: [...(valid[0] || data.variants[0]).opts], opener };
  renderPicker();
  pickerDlg.showModal();
};

// ---- "Complementa tu compra" (aa-complement): flechas del carrusel.
const initComplement = (root = document) => {
  root.querySelectorAll('[data-aa-cmp]:not([data-aa-ready])').forEach((box) => {
    box.dataset.aaReady = '1';
    const track = box.querySelector('[data-aa-cmp-track]');
    const prev = box.querySelector('[data-aa-cmp-prev]');
    const next = box.querySelector('[data-aa-cmp-next]');
    if (!track) return;
    const sync = () => {
      prev.disabled = track.scrollLeft < 4;
      next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
    };
    const step = (dir) => track.scrollBy({ left: dir * Math.max(track.clientWidth - 60, 120), behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    prev?.addEventListener('click', () => step(-1));
    next?.addEventListener('click', () => step(1));
    track.addEventListener('scroll', sync, { passive: true });
    sync();
    box.addEventListener('click', (event) => {
      const pick = event.target.closest('[data-aa-cmp-pick]');
      if (!pick) return;
      try {
        openVariantPicker(JSON.parse(pick.parentElement.querySelector('[data-aa-cmp-data]').textContent), pick);
      } catch {}
    });
  });
};
initComplement();

// ---- Ficha andes en celular (28/10/2026, Elías). Solo product.andes y pantallas de menos de 750 px; estilos al final
// de aa-producto.css. Barra fija con "Comprar ahora", contador de fotos,
// aviso al agregar al carrito y "Ver más" en descripciones largas.
const ANDES_MAIN = document.querySelector('main[data-template="product.andes"]');
const mobileMq = window.matchMedia('(max-width: 749px)');

// Contador "2/4" sobre la foto principal.
function initGalleryCount() {
  const gallery = ANDES_MAIN?.querySelector('media-gallery');
  const slides = gallery?.querySelectorAll('slideshow-slide:not([hidden])');
  if (!gallery || !slides || slides.length < 2 || gallery.querySelector('.aa-gal-count')) return;
  const box = gallery.querySelector('slideshow-container') || gallery;
  const count = document.createElement('span');
  count.className = 'aa-gal-count';
  count.setAttribute('aria-hidden', 'true');
  box.append(count);
  const paint = (i = 0) => {
    const visible = [...gallery.querySelectorAll('slideshow-slide:not([hidden])')];
    count.textContent = `${Math.min(i, visible.length - 1) + 1}/${visible.length}`;
  };
  paint();
  gallery.addEventListener('slideshow:select', (e) => paint(e.detail?.index ?? 0));
}

// Barra fija inferior: carrito como ícono y "Comprar ahora" (usa el botón principal, con su variante y cantidad).
function initStickyBuy() {
  const bar = ANDES_MAIN?.querySelector('.sticky-add-to-cart__bar');
  const atc = bar?.querySelector('.sticky-add-to-cart__button');
  if (!atc || bar.querySelector('[data-aa-sticky-buy]')) return;
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'aa-sticky-buy';
  btn.dataset.aaStickyBuy = '';
  btn.innerHTML = '<svg viewBox="0 0 24 24" width="17" height="17" fill="currentColor" aria-hidden="true" focusable="false"><path d="M13.2 2.5 4.6 13.4c-.4.5 0 1.1.6 1.1h5.3l-1.2 6.9c-.1.7.8 1 1.2.5l8.6-10.9c.4-.5 0-1.1-.6-1.1h-5.3l1.2-6.9c.1-.7-.8-1-1.2-.5Z"/></svg><span>Comprar ahora</span>';
  atc.after(btn);
  atc.setAttribute('aria-label', 'Agregar al carrito');
  btn.addEventListener('click', () => ANDES_MAIN.querySelector('.product-form-buttons [data-aa-buy-now]')?.click());
  const price = bar.querySelector('.sticky-add-to-cart__price');
  if (price) {
    paintStickyOff(price);
    new MutationObserver(() => paintStickyOff(price)).observe(price, { childList: true, subtree: true });
  }
}

// Escritorio: la columna de compra es fija, así que la barra de Horizon casi nunca aparecía (se oculta 200 px antes
// del pie). Aquí se muestra apenas los botones quedan arriba de la pantalla y se oculta solo cuando el pie ya se ve.
function initDesktopSticky() {
  const bar = ANDES_MAIN?.querySelector('.sticky-add-to-cart__bar');
  const buy = ANDES_MAIN?.querySelector('.buy-buttons-block');
  const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
  if (!bar || !buy || !footer) return;
  let queued = false;
  const update = () => {
    queued = false;
    if (mobileMq.matches) return;
    const show = buy.getBoundingClientRect().bottom < 0 && footer.getBoundingClientRect().top > window.innerHeight;
    if ((bar.dataset.stuck === 'true') !== show) bar.dataset.stuck = String(show);
  };
  window.addEventListener('scroll', () => { if (!queued) { queued = true; requestAnimationFrame(update); } }, { passive: true });
}

// "-23%" junto al precio tachado de la barra fija: solo con precio comparación real (mayor que el precio).
const cents = (el) => Number((el?.textContent || '').replace(/\D/g, '')) || 0;
function paintStickyOff(price) {
  const box = price.querySelector("[ref='priceContainer']") || price;
  const now = cents(box.querySelector('.price-item__group.price bdi'));
  const was = cents(box.querySelector('.compare-at-price bdi'));
  const pct = now && was > now ? Math.round((1 - now / was) * 100) : 0;
  const tag = box.querySelector('.aa-stick-off');
  const text = pct >= 1 ? `-${pct}%` : '';
  if ((tag?.textContent || '') === text) return;
  tag?.remove();
  if (!text) return;
  const off = document.createElement('span');
  off.className = 'aa-stick-off';
  off.textContent = text;
  off.setAttribute('aria-label', `${pct}% de descuento`);
  box.append(off);
}

// Aviso al agregar al carrito: pequeño, sobre la barra fija, se va solo a los 4 s.
let toastTimer;
function showAddedToast() {
  if (!ANDES_MAIN || !mobileMq.matches) return;
  let toast = document.querySelector('.aa-added-toast');
  if (!toast) {
    toast = document.createElement('div');
    toast.className = 'aa-added-toast';
    toast.setAttribute('role', 'status');
    const cartUrl = (typeof Theme !== 'undefined' && Theme.routes?.cart_url) || '/cart';
    toast.innerHTML = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12.5l4.2 4.2L19 7"/></svg><span>Agregado al carrito</span><a href="${cartUrl}">Ir a pagar</a>`;
    document.body.append(toast);
  }
  toast.classList.remove('is-on');
  void toast.offsetWidth;
  toast.classList.add('is-on');
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => toast.classList.remove('is-on'), 4000);
}
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (!(event.target instanceof Element) || !event.target.closest('main[data-template="product.andes"]')) return;
  event.promise?.then((r) => !r?.detail?.didError && showAddedToast()).catch(() => {});
});

// "Ver más" solo cuando la descripción es muy larga (más de ~1,5 pantallas).
function initLongDescription() {
  const panel = ANDES_MAIN?.querySelector('.aa-pd-tabs__panel.rte');
  if (!panel || panel.dataset.aaClamp || !mobileMq.matches || panel.scrollHeight < window.innerHeight * 1.5) return;
  panel.dataset.aaClamp = 'closed';
  const more = document.createElement('button');
  more.type = 'button';
  more.className = 'aa-more';
  more.setAttribute('aria-expanded', 'false');
  more.textContent = 'Ver más';
  panel.after(more);
  more.addEventListener('click', () => {
    const open = panel.dataset.aaClamp === 'closed';
    panel.dataset.aaClamp = open ? 'open' : 'closed';
    more.setAttribute('aria-expanded', String(open));
    more.textContent = open ? 'Ver menos' : 'Ver más';
    if (!open) panel.scrollIntoView({ block: 'start', behavior: reduceMotion.matches ? 'auto' : 'smooth' });
  });
}

// Velocidad (30/10/2026): desde la 4.ª foto la galería carga diferida (product-media-gallery-content.liquid).
// Al tocar, deslizar o elegir una miniatura se adelantan la foto elegida y las 2 siguientes: nunca se ve un hueco.
function eagerSlides(gallery, from) {
  const slides = gallery?.querySelectorAll('slideshow-slide');
  if (!slides) return;
  for (let i = Math.max(0, from); i < Math.min(slides.length, from + 3); i++) {
    slides[i].querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
  }
}
document.addEventListener('slideshow:select', (e) => {
  const gallery = e.target instanceof Element ? e.target.closest('media-gallery') : null;
  if (gallery) eagerSlides(gallery, e.detail?.index ?? 0);
});
document.addEventListener('pointerdown', (e) => {
  const gallery = e.target instanceof Element ? e.target.closest('media-gallery') : null;
  if (gallery) eagerSlides(gallery, (gallery.slideshow?.current ?? 0) + 1);
}, { passive: true });

if (ANDES_MAIN) {
  initGalleryCount();
  initStickyBuy();
  initDesktopSticky();
  if (document.readyState === 'complete') initLongDescription();
  else window.addEventListener('load', initLongDescription, { once: true });
}
